import React from 'react';
import Kanban from 'lucide-react/dist/esm/icons/kanban';
import Archive from 'lucide-react/dist/esm/icons/archive';
import Radar from 'lucide-react/dist/esm/icons/radar';
import Settings from 'lucide-react/dist/esm/icons/settings';
import { Text } from 'components/ui';
import { useUIStore } from 'stores';
import { usePermissions } from 'hooks/usePermissions';
import { Permissions } from 'config/permissions';
import { getTranslation } from 'i18n';
import { useLeadGeneratorStore } from '../stores/leadGeneratorStore';
import type { SalesSection } from '../types/sales';

interface SidebarItem {
  id: SalesSection;
  labelKey: string;
  descKey: string;
  icon: React.FC<{ size?: number; strokeWidth?: number }>;
  permission?: string;
}

const SECTIONS: SidebarItem[] = [
  { id: 'pipeline', labelKey: 'salesSidebarPipelineLabel', descKey: 'salesSidebarPipelineDesc', icon: Kanban },
  { id: 'archived', labelKey: 'salesSidebarArchivedLabel', descKey: 'salesSidebarArchivedDesc', icon: Archive },
  { id: 'generator', labelKey: 'salesSidebarGeneratorLabel', descKey: 'salesSidebarGeneratorDesc', icon: Radar },
  {
    id: 'settings',
    labelKey: 'salesSidebarSettingsLabel',
    descKey: 'salesSidebarSettingsDesc',
    icon: Settings,
    permission: Permissions.SALES_SETTINGS_MANAGE,
  },
];

const SalesSidebar: React.FC = () => {
  const salesSection = useUIStore((s) => s.salesSection);
  const setSalesSection = useUIStore((s) => s.setSalesSection);
  const language = useUIStore((s) => s.language);
  const t = (key: string) => getTranslation(language, key);
  const { hasPermission } = usePermissions();
  const generatorPhase = useLeadGeneratorStore((s) => s.phase);

  const visibleSections = SECTIONS.filter(
    (s) => !s.permission || hasPermission(s.permission as any),
  );

  return (
    <aside className="fixed left-[60px] top-[60px] w-[240px] h-[calc(100vh-60px)] bg-surface-page border-r border-subtle z-[50] flex flex-col overflow-hidden">
      <div className="px-4 pt-5 pb-3">
        <Text variant="subheading">{t('salesSidebarTitle')}</Text>
      </div>

      <nav className="flex-1 overflow-y-auto px-2">
        {visibleSections.map((section) => {
          const isActive = salesSection === section.id;
          const Icon = section.icon;

          return (
            <button
              key={section.id}
              onClick={() => setSalesSection(section.id)}
              className={`
                w-full flex items-start gap-3 px-3 py-2.5 rounded-lg mb-0.5 text-left
                transition-all duration-150 border-none outline-none cursor-pointer
                ${isActive
                  ? 'bg-status-success/10 text-status-success'
                  : 'bg-transparent text-content-secondary hover:bg-surface-primary hover:text-content'
                }
              `}
            >
              <div className="mt-0.5 flex-shrink-0">
                {section.id === 'generator' && generatorPhase === 'running' && !isActive ? (
                  <div className="relative w-4 h-4">
                    <Radar size={16} className="text-purple-500 animate-spin" style={{ animationDuration: '3s' }} />
                    <div className="absolute inset-0 bg-purple-500/20 rounded-full animate-ping" />
                  </div>
                ) : (
                  <Icon size={16} strokeWidth={isActive ? 2.2 : 1.8} />
                )}
              </div>
              <div className="min-w-0">
                <div className={`text-sm font-medium ${isActive ? 'text-status-success' : ''}`}>
                  {t(section.labelKey)}
                </div>
                <div className="text-xs text-content-muted mt-0.5 truncate">
                  {t(section.descKey)}
                </div>
              </div>
              {isActive && (
                <span className="ml-auto mt-1.5 w-1.5 h-1.5 rounded-full bg-status-success flex-shrink-0" />
              )}
            </button>
          );
        })}
      </nav>
    </aside>
  );
};

export default React.memo(SalesSidebar);
